'use client';

import {
  Comment01Icon,
  Infinity01Icon,
  LeftToRightListBulletIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import {
  COPILOT_MODE_OPTIONS,
  DEFAULT_COPILOT_MODE,
  type CopilotMode,
} from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { ComposerPillMenu, type ComposerPillOption } from './ComposerPillMenu';

type ModeSelectorProps = {
  chatId: string;
};

type SelectableCopilotMode = Exclude<CopilotMode, 'ask'>;

const SELECTABLE_MODES = COPILOT_MODE_OPTIONS.filter(
  (mode): mode is SelectableCopilotMode => mode !== 'ask',
);

const MODE_ICONS: Record<SelectableCopilotMode, TerraIcon> = {
  agent: Infinity01Icon,
  plan: LeftToRightListBulletIcon,
};

export function ModeSelector({ chatId }: ModeSelectorProps) {
  const tMode = useTranslations('chat.copilot.mode');
  const storedMode = useChatInstancesStore(
    (state) => state.configs.get(chatId)?.mode ?? DEFAULT_COPILOT_MODE,
  );
  const setChatMode = useChatInstancesStore((state) => state.setChatMode);
  const options: ComposerPillOption<CopilotMode>[] = SELECTABLE_MODES.map(
    (mode) => ({
      value: mode,
      label: tMode(`${mode}Label`),
      description: tMode(`${mode}Description`),
      icon: MODE_ICONS[mode],
      tone: 'gray',
    }),
  );
  const storedAskOption: ComposerPillOption<CopilotMode> = {
    value: 'ask',
    label: tMode('askLabel'),
    description: tMode('askDescription'),
    icon: Comment01Icon,
    tone: 'gray',
  };

  return (
    <ComposerPillMenu
      ariaLabel={tMode('selectorLabel')}
      options={options}
      value={storedMode}
      triggerOption={storedMode === 'ask' ? storedAskOption : undefined}
      onValueChange={(mode) => setChatMode(chatId, mode)}
      contentWidth="240px"
    />
  );
}
